import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { pickLocale } from "../lib/format";
import { SmallCaps } from "../components/atelier/primitives";
import Header from "../components/layout/Header";
import AdfImage from "../components/storefront/AdfImage";
import Footer from "../components/layout/Footer";
import ProductFilters, { clearProductFilters, getProductFilterQuery } from "../components/storefront/ProductFilters";
import ProductListingShell, { ProductListingResults } from "../components/storefront/ProductListingShell";
import SeoMeta from "../components/storefront/SeoMeta";
import { buildListingJsonLd } from "../lib/listingJsonLd";
import Pagination, { PAGE_SIZE } from "../components/storefront/Pagination";
import { listingHeroImage, ss26Image } from "../components/storefront/assets";
import { useI18n } from "../lib/i18n";
import { useDocumentTitle } from "../lib/useDocumentTitle";

const labelFromHandle = (handle = "") => handle
  .split("-")
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(" ");

function normaliseHeroSlides(value, fallback) {
  const rows = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(/[\n,]+/)
      : [];
  const slides = rows.map((item) => String(item || "").trim()).filter(Boolean);
  return slides.length ? slides : [fallback].filter(Boolean);
}

export function CollectionsIndex() {
  const { t } = useI18n();
  const { locale } = useI18n();
  useDocumentTitle(t("collections.doc_title", "Kolleksiyalar"));
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/collections").then(r => setItems(r.data || [])); }, []);
  const featured = items[0];
  const rest = featured ? items.slice(1) : items;
  return (
    <div className="bg-ivory text-graphite" data-testid="collections-index">
      <Header />
      <main id="main">
      <section className="mx-auto grid max-w-[1440px] gap-10 px-6 py-16 lg:grid-cols-12 lg:px-12 lg:py-24">
        <div className="lg:col-span-7">
          <SmallCaps>{t("collections.title", "Kolleksiya bələdçisi")}</SmallCaps>
          <h1 className="mt-5 font-serif text-5xl leading-[1.02] lg:text-7xl">
            {t("collections.eyebrow", "Mövsümü parça, siluet və istifadə məqsədinə görə seçin.")}
          </h1>
        </div>
        <div className="border-t border-graphite/15 pt-6 lg:col-span-5 lg:self-end">
          <p className="max-w-xl text-[15px] font-light leading-7 text-graphite/75">
            {t("collections.intro", "Bu səhifə ayrı-ayrı məhsul kartları deyil, hazır qarderob istiqamətləridir. Hər kolleksiya uyğun parçaları bir yerdə göstərir və məhsula keçidi sürətləndirir.")}
          </p>
          {/* Only the count that is actually counted. This strip used to sit
              beside it advertising "24+ seçilmiş look" and "1 qısa yol" —
              both literals in the markup, neither derived from anything. With
              no collections published the page read "0 kolleksiya · 24+
              seçilmiş look", which is a number the shop cannot stand behind.
              With nothing to count, the empty state below says so on its own. */}
          {items.length > 0 && (
            <div className="mt-8 border-y border-graphite/10 py-4 text-sm text-graphite/70">
              <strong className="font-serif text-3xl font-normal text-graphite">{items.length}</strong>
              <span className="ml-3">{t("collections.metric.chapters", "kolleksiya")}</span>
            </div>
          )}
        </div>
      </section>
      <section className="mx-auto max-w-[1440px] px-6 pb-32 lg:px-12">
        {featured && (
          <Link
            to={`/collections/${featured.handle}`}
            data-testid={`collection-${featured.handle}`}
            className="group grid border border-graphite/10 bg-paper lg:grid-cols-12"
          >
            <div className="relative min-h-[460px] overflow-hidden lg:col-span-7">
              <AdfImage
                source={featured.cover_image_url || ss26Image(12)}
                alt={pickLocale(featured.title, locale)}
                sizes="(min-width: 1024px) 60vw, 100vw"
                priority
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.025]"
              />
            </div>
            <div className="flex min-h-[460px] flex-col justify-end p-8 lg:col-span-5 lg:p-12">
              <SmallCaps>{t("collections.featured", "Seçilmiş istiqamət")}</SmallCaps>
              <h2 className="mt-4 font-serif text-5xl leading-[0.98] lg:text-6xl">{pickLocale(featured.title, locale)}</h2>
              <p className="mt-6 max-w-md text-[15px] font-light leading-7 text-graphite/75">{pickLocale(featured.editorial_intro, locale)}</p>
              <span className="mt-10 inline-flex min-h-11 items-center text-[11px] uppercase tracking-widest text-espresso">
                {t("collections.open", "Kolleksiyanı aç")}
              </span>
            </div>
          </Link>
        )}
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((c, idx) => (
            <Link
              key={c.id}
              to={`/collections/${c.handle}`}
              data-testid={`collection-${c.handle}`}
              className="group block border border-graphite/10 bg-paper transition-colors hover:border-graphite/35"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <AdfImage
                  source={c.cover_image_url || ss26Image(idx + 14)}
                  alt={pickLocale(c.title, locale)}
                  sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.035]"
                />
              </div>
              <div className="p-6">
                <SmallCaps>{t("collections.chapter", "Kolleksiya")}</SmallCaps>
                <div className="mt-3 font-serif text-4xl leading-none">{pickLocale(c.title, locale)}</div>
                <p className="mt-4 line-clamp-3 text-sm font-light leading-6 text-graphite/70">{pickLocale(c.editorial_intro, locale)}</p>
              </div>
            </Link>
          ))}
        </div>
        {!items.length && (
          <div className="border border-graphite/10 bg-paper p-10">
            <SmallCaps>{t("collections.empty.eyebrow", "Kolleksiyalar")}</SmallCaps>
            <p className="mt-4 font-serif text-3xl">{t("collections.empty.title", "Yeni mövsüm istiqamətləri hazırlanır.")}</p>
          </div>
        )}
      </section>
      </main>
      <Footer />
    </div>
  );
}

export function CollectionDetail() {
  const { t } = useI18n();
  const { handle } = useParams();
  const { locale } = useI18n();
  const [sp, setSp] = useSearchParams();
  const [collection, setCollection] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [collectionLoading, setCollectionLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const queryParams = useMemo(() => ({
    collection: handle,
    page: Math.max(1, Number(sp.get("page")) || 1),
    page_size: PAGE_SIZE,
    sort: sp.get("sort") || (handle === "best-sellers" ? "bestselling" : "newest"),
    ...getProductFilterQuery(sp),
  }), [handle, sp]);
  const filterScope = useMemo(() => ({ collection: handle }), [handle]);

  useEffect(() => {
    let active = true;
    setCollectionLoading(true);
    api.get(`/collections/${handle}`)
      .then((response) => { if (active) setCollection(response.data); })
      .catch(() => { if (active) setCollection(null); })
      .finally(() => { if (active) setCollectionLoading(false); });
    return () => { active = false; };
  }, [handle]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    api.get("/products", { params: queryParams })
      .then((response) => {
        if (!active) return;
        const items = response.data.items || [];
        setProducts(items);
        setTotal(Number(response.data.total ?? items.length));
      })
      .catch(() => {
        if (!active) return;
        setProducts([]);
        setTotal(0);
        setLoadError(true);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [queryParams, retryKey]);

  if (collectionLoading) {
    return (
      <div className="min-h-screen bg-ivory text-graphite">
        <Header />
        <main id="main" className="adf-container py-16">
          <div className="h-[520px] animate-pulse bg-[var(--adf-gray-6)]" />
        </main>
        <Footer />
      </div>
    );
  }

  // An empty listing must not be indexed. `selection-of-the-moment` ships with
  // zero products and was served `index, follow`, so a search engine was invited
  // to rank a page that shows nothing — and its own copy promises "four
  // signature pieces". `follow` stays: the links out of it are still worth
  // crawling. Restored automatically the moment the collection is populated.
  const listingIsEmpty = !loading && !loadError && products.length === 0;
  const seo = {
    ...(collection?.seo ?? {}),
    ...(listingIsEmpty ? { index: false } : {}),
  };
  const title = pickLocale(seo.h1, locale) || pickLocale(collection?.title, locale) || labelFromHandle(handle);
  const intro = pickLocale(collection?.editorial_intro, locale)
    || pickLocale(seo.seo_intro, locale)
    || (locale === "az"
      ? "Bu istiqamətdə seçilmiş məhsulları ölçü, rəng, material və xidmətlər üzrə süzün."
      : "Filter the selected edit by size, colour, material and services.");
  const bottomContent = pickLocale(seo.bottom_content, locale);
  // SeoMeta needs a plain URL for og:image, so unwrap the descriptor.
  const coverSource = seo.listing_hero_image || collection?.cover_image_url || listingHeroImage(handle === "best-sellers" ? 3 : 4);
  const heroSlides = normaliseHeroSlides(seo.listing_hero_slides, coverSource);
  const cover = typeof coverSource === "string" ? coverSource : coverSource?.src;
  const heroNote = pickLocale(seo.listing_hero_note, locale);
  return (
    <div className="bg-[var(--adf-white)] text-[var(--adf-ink)]" data-testid="collection-detail">
      <SeoMeta
        title={collection?.seo_title || collection?.title || title}
        description={collection?.seo_description || collection?.editorial_intro || intro}
        seo={seo}
        locale={locale}
        canonicalPath={`/collections/${handle}`}
        image={cover}
        jsonLd={buildListingJsonLd({
          kind: "collection", handle, title, description: intro, products, locale,
          label: locale === "az" ? "Kolleksiyalar" : "Collections",
        })}
      />
      <Header />
      <main id="main">
      <ProductListingShell
        title={title}
        eyebrow={t("collections.chapter", "Kolleksiya")}
        // "Atelyenin bu mövsüm üçün seçdiyi dörd imza parçası" printed above an
        // empty grid on `selection-of-the-moment`: the page promised four
        // pieces and delivered none. The editor's copy is theirs to keep — it
        // becomes true again the moment the collection is filled — so it is
        // withheld while the shelf is bare rather than rewritten.
        description={listingIsEmpty ? "" : intro}
        image={coverSource}
        heroSlides={heroSlides}
        imageFit={seo.listing_hero_image_fit || "contain"}
        imagePosition={seo.listing_hero_image_position || "center"}
        imageHeight={seo.listing_hero_image_height || 420}
        heroNote={heroNote}
        total={total}
        loading={loading}
        toolbar={(
          <ProductFilters
            searchParams={sp}
            setSearchParams={setSp}
            total={total}
            loading={loading}
            scopeParams={filterScope}
          />
        )}
        testId={`collection-listing-${handle}`}
      >
        <ProductListingResults
          listName="collection"
          products={products}
          loading={loading}
          loadError={loadError}
          onRetry={() => setRetryKey((value) => value + 1)}
          onClear={() => clearProductFilters(sp, setSp)}
          productTestId="collection-product"
          emptyTitle={t("collections.empty.products.title", "Bu seçim üçün məhsul tapılmadı.")}
          emptyDescription={t("collections.empty.products.help", "Filterləri azaldın və ya bütün kataloqa keçin.")}
          emptyActions={<Link to="/shop" className="adf-cta adf-cta-outline">{t("collections.all_products", "Bütün məhsullar")}</Link>}
        />

        <Pagination
          page={queryParams.page}
          total={total}
          onChange={(next) => {
            const spNext = new URLSearchParams(sp);
            if (next <= 1) spNext.delete("page"); else spNext.set("page", String(next));
            setSp(spNext);
            document.getElementById("main")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          testId="collection-pagination"
        />

        {bottomContent && (
          <div className="mt-20 max-w-3xl border-t border-graphite/15 pt-12 text-sm leading-7 text-graphite/75">
            {bottomContent}
          </div>
        )}
      </ProductListingShell>
      </main>
      <Footer />
    </div>
  );
}
